<template>
  <div class="rds-page">

    <!-- Active section title -->
    <h2 class="rds-title">
      <span v-if="activeTab === 'actuals'">Actuals Based Reports</span>
      <span v-else-if="activeTab === 'planned'">Planned Data Based Reports</span>
      <span v-else-if="activeTab === 'otp'">OTP Data Based Reports</span>
      <span v-else-if="activeTab === 'misc'">Miscellaneous Reports</span>
    </h2>

    <!-- Category filter pills -->
    <div class="rds-pill-bar">
      <button
        v-for="tab in visibleTabs"
        :key="tab.id"
        class="rds-pill"
        :class="{ active: activeTab === tab.id }"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Actuals tab -->
    <div v-if="activeTab === 'actuals'" class="rds-section">
      <p class="rds-section-sub">Reports generated from real-world actuals matched against planned bookings.</p>
      <div class="rds-card-grid">
        <router-link v-for="card in actualsCards" :key="card.route" :to="{ name: card.route }" class="rds-card">
          <div class="rds-card-title">{{ card.name }}</div>
          <div class="rds-card-desc">{{ card.description }}</div>
        </router-link>
      </div>
    </div>

    <!-- Planned tab -->
    <div v-if="activeTab === 'planned'" class="rds-section">
      <p class="rds-section-sub">Reports derived from scheduled bookings and planned fleet utilisation.</p>
      <div class="rds-card-grid">
        <router-link v-for="card in plannedCards" :key="card.route" :to="{ name: card.route }" class="rds-card">
          <div class="rds-card-title">{{ card.name }}</div>
          <div class="rds-card-desc">{{ card.description }}</div>
        </router-link>
      </div>
    </div>

    <!-- OTP tab -->
    <div v-if="activeTab === 'otp'" class="rds-section">
      <p class="rds-section-sub">On-time performance reports using route and telematics data.</p>
      <div class="rds-card-grid">
        <router-link v-for="card in otpCards" :key="card.route" :to="{ name: card.route }" class="rds-card">
          <div class="rds-card-title">{{ card.name }}</div>
          <div class="rds-card-desc">{{ card.description }}</div>
        </router-link>
      </div>
    </div>

    <!-- Misc tab -->
    <div v-if="activeTab === 'misc'" class="rds-section">
      <p class="rds-section-sub">Additional fleet reports covering unplanned activity, maintenance, and event history.</p>
      <div class="rds-card-grid">
        <router-link v-for="card in miscCards" :key="card.route" :to="{ name: card.route }" class="rds-card">
          <div class="rds-card-title">{{ card.name }}</div>
          <div class="rds-card-desc">{{ card.description }}</div>
        </router-link>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'report_dashboard_shell',
  data() {
    return {
      activeTab: null,
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    tenant_type() { return this.user.types[this.user.tenant] },
    telematics_type() { return this.user.telematics_types[this.user.tenant] },
    features() { return this.user.features[this.user.tenant] || {} },
    isEffectiveAdmin() { return this.user.role === 'admin' && this.viewAs !== 'non-admin' },
    isTbn() { return this.tenant_type === 'TBN' },
    isCmFleet() { return this.tenant_type === 'CM-FLEET' },
    isParentTbn() { return this.isTbn && this.user.tenant.indexOf('parent') !== -1 },
    restrictedRole() {
      if (this.isEffectiveAdmin) return null
      if (this.isCmFleet) return null
      const r = this.user.role
      if (r === 'garage') return 'garage'
      if (this.isParentTbn && r !== 'customer-admin') return 'tbn-parent'
      if (r === 'manager') return 'manager'
      return null
    },
    showActuals() {
      if (this.restrictedRole === 'garage') return false
      if (this.isCmFleet) return false
      if (this.restrictedRole === 'manager' || this.restrictedRole === 'tbn-parent') return true
      return !!this.features.actuals_enabled
    },
    showPlanned() {
      if (this.restrictedRole === 'manager' || this.restrictedRole === 'tbn-parent') return false
      if (this.isCmFleet) return false
      return true
    },
    showOtp() {
      if (this.restrictedRole) return false
      return !!this.features.views_reports_otp_report_enabled || this.isCmFleet
    },
    showMisc() {
      if (this.restrictedRole === 'garage' || this.restrictedRole === 'manager') return false
      if (this.isCmFleet) return false
      return true
    },
    visibleTabs() {
      const tabs = []
      if (this.showActuals) tabs.push({ id: 'actuals', label: 'Actuals' })
      if (this.showPlanned) tabs.push({ id: 'planned', label: 'Planned' })
      if (this.showOtp)     tabs.push({ id: 'otp',     label: 'OTP' })
      if (this.showMisc)    tabs.push({ id: 'misc',    label: 'Misc' })
      return tabs
    },
    actualsCards() {
      if (this.restrictedRole === 'manager' || this.restrictedRole === 'tbn-parent') {
        if (this.isTbn) {
          return [
            { name: 'Driver Daily Report',    route: 'actuals_driver_payroll_report_tbn', description: 'Driver activity and hours for a single day.' },
            { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report_tbn',description: 'Driver activity and hours across multiple days.' },
          ]
        }
        return [
          { name: 'Driver Daily Report',    route: 'actuals_driver_payroll_report', description: 'Driver activity and hours for a single day.' },
          { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report',description: 'Driver activity and hours across multiple days.' },
        ]
      }
      if (this.isTbn) {
        return [
          { name: 'Daily OTP Report',       route: 'actuals_perf_report_tbn',           description: 'Compare planned booking times against actual on-time performance by day.' },
          { name: 'Multiday OTP Report',    route: 'actuals_multiday_otp_report_tbn',   description: 'On-time performance trends across a custom date range.' },
          { name: 'Pickup/Dropoff Report',  route: 'actuals_billing_report_tbn',        description: 'Pickup and dropoff event details per booking.' },
          { name: 'Start Events Report',    route: 'actuals_start_events_report_tbn',   description: 'Vehicle start events matched to scheduled bookings.' },
          { name: 'Driver Daily Report',    route: 'actuals_driver_payroll_report_tbn', description: 'Driver activity and hours for a single day.' },
          { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report_tbn',description: 'Driver activity and hours across multiple days.' },
          { name: 'Payroll Summary Report', route: 'actuals_payroll_summary_report_tbn',description: 'Aggregated payroll totals by driver for a date range.' },
        ]
      }
      const cards = [
        { name: 'Daily OTP Report',       route: 'actuals_perf_report',           description: 'Compare planned booking times against actual on-time performance by day.' },
        { name: 'Multiday OTP Report',    route: 'actuals_multiday_otp_report',   description: 'On-time performance trends across a custom date range.' },
        { name: 'Mileage By Booking',     route: 'actuals_mileage_by_booking_report', description: 'Per-booking mileage breakdown for accurate client billing.' },
        { name: 'Billing Report',         route: 'actuals_billing_report',        description: 'Summarised billing data based on completed actuals.' },
        { name: 'Vehicle Start/Stop',     route: 'actuals_vehicle_report',        description: 'Vehicle engine start and stop events per booking.' },
        { name: 'Driver Daily Report',    route: 'actuals_driver_payroll_report', description: 'Driver activity and hours for a single day.' },
        { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report',description: 'Driver activity and hours across multiple days.' },
        { name: 'Payroll Summary Report', route: 'actuals_payroll_summary_report',description: 'Aggregated payroll totals by driver for a date range.' },
      ]
      if (this.telematics_type === 'SAMSARA') {
        cards.splice(2, 0, { name: 'HOS Event History', route: 'hos_history_report', description: 'Hours of service events for SAMSARA-equipped vehicles.' })
      }
      return cards
    },
    plannedCards() {
      if (this.restrictedRole === 'garage') {
        return [{ name: 'Vehicle Utilization Weekly', route: 'planned_vehicle_weekly', description: 'Weekly view of vehicle fleet utilisation.' }]
      }
      if (this.isTbn) {
        return [
          { name: 'Driver Utilization Daily',   route: 'planned_drivers_daily_tbn',          description: 'Daily view of driver utilisation against planned bookings.' },
          { name: 'Driver Utilization Weekly',  route: 'planned_drivers_weekly_util_tbn',     description: 'Weekly driver utilisation across the fleet.' },
          { name: 'Driver Utilization Monthly', route: 'planned_driver_types_monthly_tbn',    description: 'Monthly breakdown of driver type utilisation.' },
          { name: 'Driver Dispatch Weekly',     route: 'planned_drivers_weekly_dispatch_tbn', description: 'Weekly driver dispatch schedule.' },
          { name: 'Vehicle Utilization Daily',  route: 'planned_vehicle_daily_tbn',           description: 'Daily vehicle usage compared to planned schedule.' },
          { name: 'Vehicle Utilization Weekly', route: 'planned_vehicle_weekly_tbn',          description: 'Weekly view of vehicle fleet utilisation.' },
          { name: 'Vehicle Utilization Monthly',route: 'planned_vehicle_types_monthly_tbn',   description: 'Monthly fleet utilisation by vehicle type.' },
          { name: 'Vehicle Departure Board',    route: 'trip_sheet_tbn',                      description: 'Scheduled departure times and vehicle assignments.' },
          { name: 'Booking Dispatch Board',     route: 'dispatch_board_tbn',                  description: 'Live dispatch board for booking management.' },
          { name: 'Booking Monthly View',       route: 'booking_monthly_view_tbn',            description: 'Monthly calendar view of all bookings.' },
        ]
      }
      return [
        { name: 'Driver Board',                route: 'driver_board',                    description: 'Real-time driver assignments and availability board.' },
        { name: 'Driver Utilization Daily',    route: 'planned_drivers_daily',           description: 'Daily view of driver utilisation against planned bookings.' },
        { name: 'Driver Utilization Monthly',  route: 'planned_driver_types_monthly_cm', description: 'Monthly breakdown of driver type utilisation.' },
        { name: 'Vehicle Utilization Daily',   route: 'planned_vehicle_daily',           description: 'Daily vehicle usage compared to planned schedule.' },
        { name: 'Vehicle Utilization Weekly',  route: 'planned_vehicle_weekly',          description: 'Weekly view of vehicle fleet utilisation.' },
        { name: 'Vehicle Utilization Monthly', route: 'planned_vehicle_types_monthly',   description: 'Monthly fleet utilisation by vehicle type.' },
        { name: 'Vehicle Departure Board',     route: 'trip_sheet',                      description: 'Scheduled departure times and vehicle assignments.' },
      ]
    },
    otpCards() {
      const cards = [
        { name: 'Route History',              route: 'vehicle_route_history', description: 'Historical route data for each vehicle.' },
        { name: 'Route Addresses',            route: 'route_addresses',       description: 'Address list for configured routes.' },
        { name: 'OTP Nightly Report History', route: 'report_otp',            description: 'Nightly OTP report run history and results.' },
        { name: 'OTP Results History',        route: 'otp_history',           description: 'Historical OTP results with trends over time.' },
      ]
      if (!this.isTbn) {
        cards.splice(1, 0, { name: 'Route Assignments', route: 'route_assignments', description: 'Route-to-vehicle assignment history.' })
      }
      return cards
    },
    miscCards() {
      if (this.restrictedRole === 'tbn-parent') {
        return [{ name: 'Event Notes History', route: 'event_notes_list', description: 'Notes and annotations recorded against fleet events.' }]
      }
      const cards = []
      if (!this.isTbn) {
        cards.push(
          { name: 'Unplanned Vehicle Activity', route: 'report_unplanned_vehicle', description: 'Vehicles that moved outside of scheduled bookings.' },
          { name: 'Unplanned Driver Activity',  route: 'report_unplanned_hos',     description: 'Driver activity recorded outside planned bookings.' },
        )
      }
      cards.push({ name: 'Event Notes History', route: 'event_notes_list', description: 'Notes and annotations recorded against fleet events.' })
      if (this.user.tenant === 'jeffersonlines-d637fdad') {
        cards.unshift(
          { name: 'Bus Tracker',             route: 'bus_tracker',              description: 'Live bus tracking view for Jefferson Lines.' },
          { name: 'Arrive/Depart Dashboard', route: 'arrivals_departures_board', description: 'Arrivals and departures live dashboard.' },
        )
      }
      if (this.isTbn) {
        cards.push({ name: 'HOS Event History', route: 'hos_history_report', description: 'Hours of service events for equipped vehicles.' })
      }
      if (this.isTbn && this.features.tbn_tyler_route_sync_enabled) {
        cards.push({ name: 'Tyler Route Processor', route: 'tyler_route_processor', description: 'Process and sync Tyler route data.' })
      }
      if (this.features.vehicle_status_management_enabled) {
        cards.push({ name: 'VSM History', route: 'vehicle_status_history', description: 'Historical vehicle status and inspection records.' })
      }
      if (this.features.views_reports_converter_enabled) {
        cards.push({ name: 'Report Converter', route: 'report_converter', description: 'Convert report formats for third-party system compatibility.' })
      }
      if (!this.isTbn && this.features.vehicle_status_management_maint_summary_enabled) {
        cards.push({ name: 'Vehicle Maintenance Summary', route: 'vehicle_maint_summary', description: 'Summary of vehicle maintenance activities and costs.' })
      }
      if (this.isTbn && (this.user.tenant === 'mtrwestern-tbn-3db5da5g7')) {
        cards.push({ name: 'Hours By Client Report',  route: 'actuals_hours_by_client_report_tbn',  description: 'Total hours worked grouped by client.' })
        cards.push({ name: 'Hours By Booking Report', route: 'actuals_hours_by_booking_report_tbn', description: 'Hours breakdown per individual booking.' })
      }
      if (this.isTbn && (this.user.tenant === 'anderson-759g468f' || this.user.role === 'admin')) {
        cards.push({ name: 'Intrastate Booking Report', route: 'intrastate_booking_report_tbn', description: 'Report on intrastate travel bookings.' })
      }
      return cards
    },
  },
  watch: {
    visibleTabs: {
      immediate: true,
      handler(tabs) {
        if (tabs.length && !tabs.find(t => t.id === this.activeTab)) {
          this.activeTab = tabs[0].id
        }
      },
    },
  },
}
</script>

<style scoped>
/* ── Page wrapper ───────────────────────────────────────────────────── */
.rds-page {
  padding-bottom: 40px;
}

.rds-title {
  font-size: 26px;
  font-weight: bold;
  color: #222;
  margin-bottom: 16px;
}

/* ── Category filter pills ──────────────────────────────────────────── */
.rds-pill-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
}

.rds-pill {
  padding: 6px 18px;
  border-radius: 20px;
  border: 1px solid #c8d8e8;
  background: #f0f5fa;
  color: #4a6785;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  outline: none;
}

.rds-pill:hover {
  background: #dce8f5;
  border-color: steelblue;
  color: steelblue;
}

.rds-pill.active {
  background: steelblue;
  border-color: steelblue;
  color: #fff;
}

/* ── Section ────────────────────────────────────────────────────────── */
.rds-section {
  margin-bottom: 8px;
}

.rds-section-sub {
  color: #555;
  font-size: 14px;
  margin-bottom: 20px;
}

/* ── Card grid ──────────────────────────────────────────────────────── */
.rds-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  padding-right: 16px;
}

.rds-card {
  border: 1px solid #d0d8e4;
  border-radius: 6px;
  padding: 16px;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 3px rgba(70,130,180,0.08);
  transition: box-shadow 0.15s, border-color 0.15s;
  cursor: pointer;
  text-decoration: none !important;
}

.rds-card:hover {
  border-color: steelblue;
  box-shadow: 0 2px 8px rgba(70,130,180,0.18);
  text-decoration: none !important;
}

.rds-card-title {
  font-size: 15px;
  font-weight: bold;
  color: #222;
  margin-bottom: 6px;
}

.rds-card-desc {
  font-size: 13px;
  color: #666;
  flex: 1;
  line-height: 1.4;
}
</style>
